import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User, makeAuthenticatedRequest } from '../services/authService';
import { habitService, Habit, HabitCompletion } from '../services/habitService';
import { goalService } from '../services/goalService';
import { Goal } from '../types/Goal';
import { taskService, Task } from '../services/taskService';
import DailyNotesService, { DailyNote } from '../services/dailyNotesService';
import { NotesService, Note } from '../services/notesService';
import { ContentBlock } from '../services/blockService';
import BlockEditor from './notes/BlockEditor';
import { useBlockManager } from '../hooks/useBlockManager';
import { BlockContainer } from '../services/blockManager';
import PageHeader from './common/PageHeader';
import { cashflowService, CashflowEntry } from '../services/cashflowService';
import { getActivityCounts } from '../services/activityService';
import { TimeLogsService } from '../services/timelogsService';
import { stockPortfolioService, StockTransaction } from '../services/stockPortfolioService';
import './Months.css';

interface MonthsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface MonthlyStats {
  habits: {
    total: number;
    completed: number;
    completionRate: number;
    habits: Habit[];
  };
  goals: {
    total: number;
    active: number;
    completed: number;
    goals: Goal[];
  };
  tasks: {
    total: number;
    completed: number;
    completionRate: number;
    tasks: Task[];
  };
  notes: {
    totalDays: number;
    daysWithNotes: number;
    notes: DailyNote[];
  };
  cashflow: {
    totalSpent: number;
    totalIncome: number;
    netCashflow: number;
    transactions: number;
  };
  blocks: {
    total: number;
  };
  lessons: {
    total: number;
  };
  activities: {
    total: number;
  };
  books: {
    totalPages: number;
    recordCount: number;
  };
  meditation: {
    totalMinutes: number;
    totalHours: number;
    sessionCount: number;
  };
  stocks: {
    buyCount: number;
    sellCount: number;
    totalTransactions: number;
    transactions: StockTransaction[];
  };
}

const Months: React.FC<MonthsProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { yearMonth } = useParams<{ yearMonth?: string }>();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(0); // 0 = current month, -1 = last month, etc.
  
  // Monthly note state
  const [monthlyNote, setMonthlyNote] = useState<Note | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Refs to prevent duplicate loading calls
  const loadingDataRef = useRef<boolean>(false);
  const loadingNoteRef = useRef<boolean>(false);
  
  // Create a ref to hold the current monthly note for the callback
  const monthlyNoteRef = useRef<Note | null>(null);
  
  // Update the ref whenever monthlyNote changes
  useEffect(() => {
    monthlyNoteRef.current = monthlyNote;
  }, [monthlyNote]);

  // Utility functions for year-month conversion
  const formatYearMonth = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  };

  const parseYearMonth = (yearMonthStr: string): { year: number; month: number } | null => {
    const match = yearMonthStr.match(/^(\d{4})-(\d{2})$/);
    if (!match) return null;
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    if (month < 1 || month > 12) return null;
    return { year, month };
  };

  const getCurrentYearMonth = (): string => {
    return formatYearMonth(new Date());
  };

  const getDateFromYearMonth = (year: number, month: number): Date => {
    return new Date(year, month - 1, 1);
  };

  const getMonthOffsetFromYearMonth = (yearMonthStr: string): number => {
    if (yearMonthStr === 'current') return 0;
    
    const parsed = parseYearMonth(yearMonthStr);
    if (!parsed) return 0;
    
    const targetDate = getDateFromYearMonth(parsed.year, parsed.month);
    const currentDate = new Date();
    const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    
    const diffMonths = (targetDate.getFullYear() - currentMonthStart.getFullYear()) * 12 + 
                      (targetDate.getMonth() - currentMonthStart.getMonth());
    return diffMonths;
  };

  const getYearMonthFromOffset = (offset: number): string => {
    const currentDate = new Date();
    const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1);
    return formatYearMonth(targetDate);
  };

  // Get month dates for a given month offset
  const getMonthDates = (monthOffset: number = 0): Date[] => {
    const currentDate = new Date();
    const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthOffset, 1);
    
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    
    // Get first and last day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const monthDates: Date[] = [];
    for (let day = 1; day <= lastDay.getDate(); day++) {
      monthDates.push(new Date(year, month, day));
    }
    
    return monthDates;
  };

  // Get month start and end dates
  const getMonthStartEndDates = (monthOffset: number = 0): { startDate: Date; endDate: Date } => {
    const currentDate = new Date();
    const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthOffset, 1);
    
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    
    return { startDate, endDate };
  };

  // Format date for API calls
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Get user ID
  const getUserId = (): number => {
    return user?.id || 0;
  };

  // Generate month range string for note ID
  const getMonthRangeId = (monthOffset: number = 0): string => {
    const { startDate, endDate } = getMonthStartEndDates(monthOffset);
    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(endDate);
    return `month-${startDateStr}-to-${endDateStr}`;
  };

  // Generate month display range
  const getMonthDisplayRange = (monthOffset: number = 0): string => {
    const { startDate, endDate } = getMonthStartEndDates(monthOffset);
    const startDateStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endDateStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startDateStr} - ${endDateStr}`;
  };

  // Block management for monthly note
  const {
    blockManager,
    contentBlocks,
    syncStatus,
    loading: blocksLoading,
    loadBlocks,
    setContainerId
  } = useBlockManager({
    userId: getUserId(),
    containerType: 'note',
    containerId: monthlyNote?.note_id,
    onContainerUpdate: async (container: BlockContainer) => {
      // Use the ref to get the current value, not the captured closure value
      const monthlyNoteValue = monthlyNoteRef.current;
      
      console.log('🔧 Updating monthly note:', container);
      console.log('🔧 Monthly note:', monthlyNoteValue);
      
      if (monthlyNoteValue) {
        const blocks = container.blocks.map((blockId, index) => ({
          block_id: blockId,
          order: index
        }));

        await NotesService.updateNote(monthlyNoteValue.note_id, {
          title: monthlyNoteValue.title,
          blocks,
          properties: monthlyNoteValue.properties
        });
      }
    }
  });

  // Load or create monthly note
  const loadMonthlyNote = useCallback(async (monthOffset: number = 0) => {
    if (!user?.id || loadingNoteRef.current) return;

    loadingNoteRef.current = true;
    setNoteLoading(true);
    setError(null);

    try {
      const userId = getUserId();
      const monthRangeId = getMonthRangeId(monthOffset);
      const monthDisplayRange = getMonthDisplayRange(monthOffset);

      try {
        // Try to load existing note
        const existingNote = await NotesService.getNoteById(monthRangeId, userId);
        setMonthlyNote(existingNote);
        setContainerId(existingNote.note_id);

        // Load the content blocks
        const blockIds = existingNote.blocks.map(b => b.block_id);
        await loadBlocks(blockIds);

      } catch (error: any) {
        // If note doesn't exist, create it
        if (error.status === 404 || error.message?.includes('not found')) {
          
          const newNote = await NotesService.createNoteWithId(
            userId,
            monthRangeId,
            `Month: ${monthDisplayRange}`,
            []
          );
          
          setMonthlyNote(newNote);
          setContainerId(newNote.note_id);
          
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      console.error('Error loading monthly note:', error);
      setError('Failed to load monthly note');
    } finally {
      loadingNoteRef.current = false;
      setNoteLoading(false);
    }
  }, [user?.id, loadBlocks, setContainerId]);

  // Load monthly data
  const loadMonthlyData = useCallback(async (monthOffset: number = 0) => {
    if (!user?.id || loadingDataRef.current) return;

    try {
      loadingDataRef.current = true;
      setLoading(true);
      setError(null);

      const userId = getUserId();
      const { startDate, endDate } = getMonthStartEndDates(monthOffset);
      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);

      // Load all data in parallel
      const [
        habitsResponse,
        completionsResponse,
        goalsResponse,
        tasksResponse,
        notesResponse,
        cashflowResponse,
        blocksResponse,
        lessonsResponse,
        activitiesResponse,
        booksResponse,
        meditationResponse,
        stockTransactionsResponse
      ] = await Promise.all([
        habitService.getUserHabits(userId, { is_active: true }),
        habitService.getCompletionsByDateRange(userId, startDateStr, endDateStr),
        goalService.getUserGoals({ user_id: userId.toString() }),
        taskService.getUserTasks({ user_id: userId.toString() }),
        DailyNotesService.getNotesInRange(userId, startDateStr, endDateStr),
        cashflowService.getCashflowEntries({
          start_date: startDateStr,
          end_date: endDateStr,
          limit: 10000,
          offset: 0
        }),
        makeAuthenticatedRequest(
          `/blocks/user/${userId}/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=month`,
          { method: 'GET' }
        ).catch(() => ({ success: false, data: [] })),
        makeAuthenticatedRequest(
          `/lessons/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=month`,
          { method: 'GET' }
        ).catch(() => ({ success: false, counts: [] })),
        getActivityCounts(startDateStr, endDateStr).catch(() => ({ success: false, counts_by_date: {}, total_count: 0 })),
        makeAuthenticatedRequest(
          `/books/user/reading-stats?start_date=${startDateStr}&end_date=${endDateStr}`,
          { method: 'GET' }
        ).catch(() => ({ success: false, total_pages: 0, record_count: 0 })),
        TimeLogsService.getTimeLogsByActivityType(userId, 'meditation', startDateStr, endDateStr).catch(() => []),
        stockPortfolioService.getUserTransactions(userId.toString(), undefined, 10000).catch(() => ({ success: false, transactions: [] }))
      ]);

      // Process habits data
      const habits = habitsResponse.success && habitsResponse.habits ? habitsResponse.habits : [];
      const completionsByDate = completionsResponse.success && completionsResponse.completions_by_date 
        ? completionsResponse.completions_by_date 
        : {};

      const monthDates = getMonthDates(monthOffset);
      let totalHabitCompletions = 0;
      let totalPossibleCompletions = 0;

      habits.forEach(habit => {
        monthDates.forEach(date => {
          const dateStr = formatDate(date);
          const dayCompletions = completionsByDate[dateStr] || [];
          const habitCompletion = dayCompletions.find(c => Number(c.habit_id) === Number(habit.id));
          
          totalPossibleCompletions++;
          if (habitCompletion && habitCompletion.is_completed) {
            totalHabitCompletions++;
          }
        });
      });

      // Process goals data
      const goals = goalsResponse.success && goalsResponse.goals ? goalsResponse.goals : [];
      const activeGoals = goals.filter(goal => goal.status === 'active');
      const completedGoals = goals.filter(goal => goal.status === 'completed');

      // Process tasks data
      // Count tasks created in the month and tasks completed in the month (using timestamps)
      const allTasks = tasksResponse.success && tasksResponse.tasks ? tasksResponse.tasks : [];
      
      // Get date strings for comparison (YYYY-MM-DD format)
      const startDateStrForComparison = formatDate(startDate);
      const endDateStrForComparison = formatDate(endDate);
      
      // Tasks created during the month
      const tasksCreatedInMonth = allTasks.filter(task => {
        if (!task.created_at) return false;
        const createdDateStr = formatDate(new Date(task.created_at));
        return createdDateStr >= startDateStrForComparison && createdDateStr <= endDateStrForComparison;
      });
      
      // Tasks completed during the month (using completed_at timestamp)
      const tasksCompletedInMonth = allTasks.filter(task => {
        if (!task.completed_at || task.status !== 'completed') return false;
        const completedDateStr = formatDate(new Date(task.completed_at));
        return completedDateStr >= startDateStrForComparison && completedDateStr <= endDateStrForComparison;
      });
      
      const taskCompletionRate = tasksCreatedInMonth.length > 0 ? (tasksCompletedInMonth.length / tasksCreatedInMonth.length) * 100 : 0;

      // Process notes data
      const notes = Array.isArray(notesResponse) ? notesResponse : [];
      const daysWithNotes = notes.length;
      const totalDays = monthDates.length;

      // Process cashflow data
      const cashflowEntries = cashflowResponse.success && cashflowResponse.cashflow ? cashflowResponse.cashflow : [];
      // Calculate spending: exclude income and investment categories, use absolute value
      const totalSpent = cashflowEntries
        .filter(entry => entry.category !== 'income' && entry.category !== 'investment')
        .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);
      // Calculate income: only entries with income category
      const totalIncome = cashflowEntries
        .filter(entry => entry.category === 'income')
        .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);
      const netCashflow = totalIncome - totalSpent;

      // Process blocks data
      const blocksData = blocksResponse.success && blocksResponse.data ? blocksResponse.data : [];
      const totalBlocks = blocksData.reduce((sum: number, item: any) => sum + (parseInt(item.count) || 0), 0);

      // Process lessons data
      const lessonsData = lessonsResponse.success && lessonsResponse.counts ? lessonsResponse.counts : [];
      const totalLessons = lessonsData.reduce((sum: number, item: any) => sum + (parseInt(item.count) || 0), 0);

      // Process activities data
      const activitiesCounts = activitiesResponse.success && activitiesResponse.counts_by_date ? activitiesResponse.counts_by_date : {};
      // Use total_count if available, otherwise sum daily counts
      const totalActivities = activitiesResponse.total_count !== undefined 
        ? activitiesResponse.total_count 
        : Object.values(activitiesCounts).reduce((sum: number, count: any) => sum + (count || 0), 0);

      // Process books data
      const booksData = booksResponse.success && booksResponse.data 
        ? booksResponse.data 
        : { total_pages: 0, record_count: 0 };
      const totalPages = booksData.total_pages || 0;
      const recordCount = booksData.record_count || 0;

      // Process meditation data
      const meditationLogs = Array.isArray(meditationResponse) ? meditationResponse : [];
      const totalMeditationMinutes = meditationLogs.reduce((sum: number, log: any) => sum + (log.duration_minutes || 0), 0);
      const totalMeditationHours = totalMeditationMinutes / 60;
      const meditationSessionCount = meditationLogs.length;

      // Process stock transactions data
      const allStockTransactions = stockTransactionsResponse.success && stockTransactionsResponse.transactions 
        ? stockTransactionsResponse.transactions 
        : [];
      
      // Filter transactions by month date range
      const stockTransactionsInMonth = allStockTransactions.filter(transaction => {
        const transactionDateStr = formatDate(new Date(transaction.transaction_date));
        return transactionDateStr >= startDateStrForComparison && transactionDateStr <= endDateStrForComparison;
      });
      
      const buyCount = stockTransactionsInMonth.filter(t => t.transaction_type === 'buy').length;
      const sellCount = stockTransactionsInMonth.filter(t => t.transaction_type === 'sell').length;
      const totalStockTransactions = stockTransactionsInMonth.length;

      const stats: MonthlyStats = {
        habits: {
          total: habits.length,
          completed: totalHabitCompletions,
          completionRate: totalPossibleCompletions > 0 ? (totalHabitCompletions / totalPossibleCompletions) * 100 : 0,
          habits: habits
        },
        goals: {
          total: goals.length,
          active: activeGoals.length,
          completed: completedGoals.length,
          goals: goals
        },
        tasks: {
          total: tasksCreatedInMonth.length,
          completed: tasksCompletedInMonth.length,
          completionRate: taskCompletionRate,
          tasks: allTasks
        },
        notes: {
          totalDays: totalDays,
          daysWithNotes: daysWithNotes,
          notes: notes
        },
        cashflow: {
          totalSpent,
          totalIncome,
          netCashflow,
          transactions: cashflowEntries.length
        },
        blocks: {
          total: totalBlocks
        },
        lessons: {
          total: totalLessons
        },
        activities: {
          total: totalActivities
        },
        books: {
          totalPages: totalPages,
          recordCount: recordCount
        },
        meditation: {
          totalMinutes: totalMeditationMinutes,
          totalHours: totalMeditationHours,
          sessionCount: meditationSessionCount
        },
        stocks: {
          buyCount: buyCount,
          sellCount: sellCount,
          totalTransactions: totalStockTransactions,
          transactions: stockTransactionsInMonth
        }
      };

      setMonthlyStats(stats);
    } catch (err) {
      console.error('Error loading monthly data:', err);
      setError('Failed to load monthly data');
    } finally {
      loadingDataRef.current = false;
      setLoading(false);
    }
  }, [user?.id]);

  // Sync URL parameter with selectedMonth
  useEffect(() => {
    if (yearMonth !== undefined) {
      if (yearMonth === 'current') {
        // Handle 'current' redirect by navigating to actual current year-month
        const currentYearMonth = getCurrentYearMonth();
        navigate(`/months/${currentYearMonth}`, { replace: true });
        return;
      }
      
      const monthOffset = getMonthOffsetFromYearMonth(yearMonth);
      if (monthOffset !== selectedMonth) {
        setSelectedMonth(monthOffset);
      }
      
      // Validate year-month format
      if (!parseYearMonth(yearMonth)) {
        // Invalid year-month format, redirect to current month
        const currentYearMonth = getCurrentYearMonth();
        navigate(`/months/${currentYearMonth}`, { replace: true });
      }
    } else {
      // No year-month parameter, redirect to current month
      const currentYearMonth = getCurrentYearMonth();
      navigate(`/months/${currentYearMonth}`, { replace: true });
    }
  }, [yearMonth, selectedMonth, navigate]);

  // Load data when component mounts or month changes
  useEffect(() => {
    if (user?.id) {
      Promise.all([
        loadMonthlyData(selectedMonth),
        loadMonthlyNote(selectedMonth)
      ]);
    }
  }, [user, selectedMonth]);

  // Navigation functions
  const goToPreviousMonth = () => {
    const newMonthOffset = selectedMonth - 1;
    const yearMonth = getYearMonthFromOffset(newMonthOffset);
    navigate(`/months/${yearMonth}`);
  };

  const goToNextMonth = () => {
    const newMonthOffset = selectedMonth + 1;
    const yearMonth = getYearMonthFromOffset(newMonthOffset);
    navigate(`/months/${yearMonth}`);
  };

  const goToCurrentMonth = () => {
    const currentYearMonth = getCurrentYearMonth();
    navigate(`/months/${currentYearMonth}`);
  };

  // Get month display info
  const getMonthDisplayInfo = () => {
    const { startDate, endDate } = getMonthStartEndDates(selectedMonth);
    
    const isCurrentMonth = selectedMonth === 0;
    const monthLabel = isCurrentMonth ? 'This Month' : 
                     selectedMonth === -1 ? 'Last Month' :
                     selectedMonth === 1 ? 'Next Month' :
                     `${Math.abs(selectedMonth)} Month${Math.abs(selectedMonth) > 1 ? 's' : ''} ${selectedMonth < 0 ? 'Ago' : 'Ahead'}`;

    return {
      monthLabel,
      startDate,
      endDate,
      isCurrentMonth
    };
  };

  const monthInfo = getMonthDisplayInfo();

  // Formatting helper functions
  const formatMeditationHours = (hours: number): string => {
    if (hours === 0) return '0 hours';
    // Round to 1 decimal place
    const roundedHours = Math.round(hours * 10) / 10;
    return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
  };

  const formatBookPages = (pages: number, sessions: number): string => {
    if (pages === 0) return '';
    if (sessions > 1) {
      return `read ${pages} pages across ${sessions} reading sessions`;
    }
    return `read ${pages} page${pages !== 1 ? 's' : ''}`;
  };

  // Generate month description paragraph
  const generateMonthDescription = (stats: MonthlyStats | null, monthOffset: number): string => {
    if (!stats) {
      return '';
    }

    const { startDate, endDate } = getMonthStartEndDates(monthOffset);
    const startDateStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endDateStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    
    const monthIdentifier = yearMonth && yearMonth !== 'current' ? yearMonth : getCurrentYearMonth();
    const monthName = startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const totalPossibleHabitCompletions = stats.habits.total * stats.notes.totalDays;
    
    const parts: string[] = [];
    
    // Month identifier and date range
    parts.push(`This is ${monthName} (${monthIdentifier}), starting from ${startDateStr} to ${endDateStr}.`);
    
    // Habits
    if (totalPossibleHabitCompletions > 0) {
      parts.push(`In this month, you completed ${stats.habits.completed} out of ${totalPossibleHabitCompletions} possible habit completions (${stats.habits.completionRate.toFixed(1)}%)`);
    } else {
      parts.push(`In this month, you have ${stats.habits.total} active habit${stats.habits.total !== 1 ? 's' : ''}`);
    }
    
    // Tasks
    if (stats.tasks.total > 0) {
      parts.push(`completed ${stats.tasks.completed} out of ${stats.tasks.total} task${stats.tasks.total !== 1 ? 's' : ''} (${stats.tasks.completionRate.toFixed(1)}%)`);
    } else {
      parts.push(`completed 0 tasks`);
    }
    
    // Goals - only mention in_progress goals
    if (stats.goals.active > 0) {
      parts.push(`maintained ${stats.goals.active} active goal${stats.goals.active !== 1 ? 's' : ''}`);
    }
    
    // Notes
    parts.push(`wrote notes on ${stats.notes.daysWithNotes} out of ${stats.notes.totalDays} day${stats.notes.totalDays !== 1 ? 's' : ''}`);
    
    // Cashflow
    if (stats.cashflow.transactions > 0) {
      const cashflowParts: string[] = [];
      if (stats.cashflow.totalIncome > 0) {
        cashflowParts.push(`earned ${stats.cashflow.totalIncome.toLocaleString()}`);
      }
      // Always mention spending if there are transactions
      if (stats.cashflow.totalSpent > 0) {
        cashflowParts.push(`spent ${stats.cashflow.totalSpent.toLocaleString()}`);
      }
      if (cashflowParts.length > 0) {
        if (cashflowParts.length > 1) {
          parts.push(`${cashflowParts.join(' and ')}, with a net cashflow of ${stats.cashflow.netCashflow >= 0 ? '+' : ''}${stats.cashflow.netCashflow.toLocaleString()}`);
        } else {
          parts.push(cashflowParts[0]);
        }
      }
    }
    
    // Blocks
    if (stats.blocks.total > 0) {
      parts.push(`created ${stats.blocks.total} block${stats.blocks.total !== 1 ? 's' : ''}`);
    }
    
    // Lessons
    if (stats.lessons.total > 0) {
      parts.push(`generated ${stats.lessons.total} lesson${stats.lessons.total !== 1 ? 's' : ''}`);
    }
    
    // Activities
    if (stats.activities.total > 0) {
      parts.push(`performed ${stats.activities.total} activit${stats.activities.total !== 1 ? 'ies' : 'y'}`);
    }
    
    // Books
    if (stats.books.totalPages > 0) {
      const bookPagesText = formatBookPages(stats.books.totalPages, stats.books.recordCount);
      if (bookPagesText) {
        parts.push(bookPagesText);
      }
    }
    
    // Meditation
    if (stats.meditation.totalHours > 0) {
      parts.push(`meditated for ${formatMeditationHours(stats.meditation.totalHours)}`);
    }
    
    // Stock transactions
    if (stats.stocks.totalTransactions > 0) {
      const stockParts: string[] = [];
      if (stats.stocks.buyCount > 0) {
        stockParts.push(`bought ${stats.stocks.buyCount} stock${stats.stocks.buyCount !== 1 ? 's' : ''}`);
      }
      if (stats.stocks.sellCount > 0) {
        stockParts.push(`sold ${stats.stocks.sellCount} stock${stats.stocks.sellCount !== 1 ? 's' : ''}`);
      }
      if (stockParts.length > 0) {
        if (stockParts.length > 1) {
          parts.push(stockParts.join(' and '));
        } else {
          parts.push(stockParts[0]);
        }
      }
    }
    
    return parts.join(', ') + '.';
  };

  if (loading) {
    return (
      <div className="notes-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<span>Monthly Progress</span>}
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading monthly data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notes-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<span>Monthly Progress</span>}
        />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-btn" onClick={() => loadMonthlyData(selectedMonth)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="notes-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="month-navigation">
            <button className="nav-btn" onClick={goToPreviousMonth}>←</button>
            <div className="month-info">
              {yearMonth && yearMonth !== 'current' && (
                <p className="year-month-label">{yearMonth}</p>
              )}
            </div>
            <button className="nav-btn" onClick={goToNextMonth}>→</button>
            {!monthInfo.isCurrentMonth && (
              <button className="current-month-btn" onClick={goToCurrentMonth}>
                Current Month
              </button>
            )}
          </div>
        }
        rightActions={
          saving ? (
            <span className="months-saving-indicator">
              <div className="saving-spinner"></div>
              Saving...
            </span>
          ) : undefined
        }
      />

      <div className="months-content">
        {/* Month Description */}
        {monthlyStats && (
          <div className="month-description">
            {generateMonthDescription(monthlyStats, selectedMonth)}
          </div>
        )}

        {/* Monthly Note Editor */}
        <div className="monthly-note-section">
          <div className="monthly-note-editor">
            {noteLoading ? (
              <div className="note-loading">
                <div className="loading-spinner"></div>
                <p>Loading monthly note...</p>
              </div>
            ) : (
              <BlockEditor
                blockManager={blockManager}
                blocks={contentBlocks}
                placeholder={`Write your notes for ${getMonthDisplayRange(selectedMonth)}...`}
                onSyncStatusChange={() => {}} // Sync status is handled by hook
              />
            )}
          </div>
        </div>
      </div>

      {/* Sync Status Icon */}
      <div className={`months-sync-status-icon ${syncStatus}`}>
        {syncStatus === 'syncing' && <div className="months-sync-spinner">⟳</div>}
        {syncStatus === 'synced' && <div className="months-sync-check">✓</div>}
        {syncStatus === 'error' && <div className="months-sync-error">⚠</div>}
      </div>

    </div>
  );
};

export default Months;

